import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import white_drop_icon from '../assets/images/whitedrop_down.svg';
import verified from '../assets/images/verified.svg';
import default_user from '../assets/images/default_user.webp';
import config from '../config/config'; 
import msgload from '../assets/images/ds.gif';


const UserHeaderDropdown = ({ user, userId, isUserPostHidden }) => {
  const { user_id } = useParams();
  const [isRestricted, setIsRestricted] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [showOnlyFree, setShowOnlyFree] = useState(false);
  const [restrictedUsers, setRestrictedUsers] = useState(new Set());
  const [blockedUsers, setBlockedUsers] = useState(new Set());
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [receiverDetails, setReceiverDetails] = useState({});
  const [toastType, setToastType] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false); 
  const [sortedUsersalldelete, setSortedUsersallDelete] = useState([]);
  const [reportReason, setReportReason] = useState('');
  const [selectedReportReason, setSelectedReportReason] = useState('');
  const [showSaveToListModal, setShowSaveToListModal] = useState({ visible: false, userId: null });
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [listName, setListName] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [userLists, setUserLists] = useState([]); 
  const [apiResponseMessage, setApiResponseMessage] = useState('');
  const navigate = useNavigate();
  const [checkedLists, setCheckedLists] = useState(new Set());
  const { username } = useParams();
  const [lockedPostsStatus, setLockedPostsStatus] = useState('active');
  const [isLoading, setIsLoading] = useState(false);  // Add this line


  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/Login');
    }
  }, [navigate]);

  const fetchUserLists = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = `${config.baseURL}/get-users-list`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // Ensure Content-Type is set if necessary
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setUserLists(data.data);
        // Initialize checkedLists state based on user list data
        const initialCheckedLists = new Set(data.data
          .filter(list => list.users.some(user => user.id === showSaveToListModal.userId))
          .map(list => list.id)
        );
        setCheckedLists(initialCheckedLists);
      } else {
        console.warn('Failed to fetch user lists:', data.message); // Add more detailed logging if needed
        setUserLists([]);
      }
    } catch (error) {
      console.error('Error fetching user lists:', error);
      setUserLists([]);
    } finally {
      setIsLoading(false);  // Stop loading
    }
  };

  useEffect(() => {
    if (showSaveToListModal) {
      fetchUserLists();
    }
  }, [showSaveToListModal]);


  const handleCheckboxChange = async (listId, userId, isChecked) => {
    setIsLoading(true);  // Start loading
    try {
      const endpoint = isChecked
        ? `${config.baseURL}/add-user-to-list`  // Add user to the list
        : `${config.baseURL}/remove-user-from-list`; // Remove user from the list
  
      const formData = new URLSearchParams();
  
      if (isChecked) {
        formData.append('user_list_id', listId);  
        formData.append('listed_user_id', userId);
      } else {
        formData.append('user_id', userId);  
        formData.append('list_id', listId);
      }
  
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      };
  
      await axios.post(endpoint, formData, { headers });
  
      // Update checkedLists state after the API call
      setCheckedLists(prev => {
        const updated = new Set(prev);
        if (isChecked) {
          updated.add(listId);  // Add listId if checked
        } else {
          updated.delete(listId);  // Remove listId if unchecked
        }
        return updated;
      });
  
      // Now update the userLists state (fetch fresh data or manipulate the list locally)
      setUserLists(prevLists => {
        return prevLists.map(list => {
          if (list.id === listId) {
            // Update user count based on whether the user is checked
            const updatedUserCount = isChecked ? list.user_count + 1 : list.user_count - 1;
            return {
              ...list,
              user_count: updatedUserCount
            };
          }
          return list;
        });
      });
    } catch (error) {
      console.error('Error updating user list:', error);
    } finally {
      setIsLoading(false);  // Stop loading
      await fetchUserLists();
    }
  };
const handleCopyLink = (userName) => {
  const profileUrl = `${window.location.origin}/${userName}`;
  
  navigator.clipboard.writeText(profileUrl)
    .then(() => {
      setToastMessage('Profile link copied to clipboard!');
      setShowToast(true);

      // Hide the success toast message after 1 second
      setTimeout(() => {
        setShowToast(false);
        setToastMessage('');
      }, 1000);
    })
    .catch(() => {
      setErrorMessage('Error copying link');
      
      // Hide the error message after 1 second
      setTimeout(() => {
        setErrorMessage('');
      }, 1000);
    });
};


const handleRestrictUser = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    const isRestricted = restrictedUsers.has(userId);
    const url = isRestricted
      ? `${config.baseURL}/unrestrict-user`
      : `${config.baseURL}/restrict-user`;
    const formData = isRestricted
      ? { unrestrict_user_id: userId }
      : { restrict_to: userId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(formData),
    });

    const data = await response.json();

    if (data.success) {
      // Update restricted users state by adding/removing the user ID
      setRestrictedUsers((prevState) => {
        const newState = new Set(prevState);
        if (isRestricted) {
          newState.delete(userId);
        } else {
          newState.add(userId);
        }
        return newState;
      });

      // Set the appropriate success message based on the action
      setToastMessage(
        isRestricted
          ? 'User unrestricted successfully!'
          : 'User restricted successfully!'
      );
      setShowToast(true);

      // Hide success message after 1 second
      setTimeout(() => {
        setShowToast(false);
        setToastMessage(''); // Clear toast message
      }, 1000);

      // Clear any error messages
      setErrorMessage('');
    } else {
      // Show error message
      setErrorMessage(
        isRestricted ? 'Failed to unrestrict user' : 'Failed to restrict user'
      );

      // Hide error message after 1 second
      setTimeout(() => {
        setErrorMessage(''); // Clear error message
      }, 1000);
    }
  } catch (error) {
    // Handle fetch error
    setErrorMessage(
      isRestricted ? 'Error unrestricting user' : 'Error restricting user'
    );

    // Hide error message after 1 second
    setTimeout(() => {
      setErrorMessage(''); // Clear error message
    }, 1000);
  }
};


const handleBlockUser = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${config.baseURL}/block-user`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ block_to: userId }),
    });

    const data = await response.json();

    if (data.success) {
      // Update blocked users state by adding the blocked user ID
      setBlockedUsers((prevState) => {
        const updatedSet = new Set(prevState);
        updatedSet.add(userId);
        return updatedSet;
      });

      // Show success toast message and hide after 1 second
      setToastMessage(data.message);
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setToastMessage(''); // Clear the toast message
      }, 1000); // 1 second delay

      setErrorMessage(''); // Clear any previous error message
    } else {
      // Show error message and hide after 1 second
      setErrorMessage(data.message);
      setTimeout(() => {
        setErrorMessage(''); // Clear the error message
      }, 1000); // 1 second delay
    }
  } catch (error) {
    // Handle fetch error
    setErrorMessage('Error blocking user');
    setTimeout(() => {
      setErrorMessage(''); // Clear the error message
    }, 1000); // 1 second delay
  }
};

  

const handleUnblockUser = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${config.baseURL}/unblock-user`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ unblock_user_id: userId }),
    });

    const data = await response.json();
    
    if (data.success) {
      // Update blocked users state by removing the unblocked user
      setBlockedUsers((prevState) => {
        const newState = new Set(prevState);
        newState.delete(userId);
        return newState;
      });

      // Show success toast message and hide after 1 second
      setToastMessage(data.message);
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setToastMessage(''); // Clear the toast message
      }, 1000); // 1 second delay

      setErrorMessage(''); // Clear any previous error message
    } else {
      // Show error message and hide after 1 second
      setErrorMessage(data.message);
      setTimeout(() => {
        setErrorMessage(''); // Clear the error message
      }, 1000); // 1 second delay
    }
  } catch (error) {
    // Handle fetch error
    setErrorMessage('Error unblocking user');
    setTimeout(() => {
      setErrorMessage(''); // Clear the error message
    }, 1000); // 1 second delay
  }
};


  const handleReportUser = (userId) => {
    setSelectedReportReason(''); // Reset the selected reason
    setReportReason(userId); // Set the userId for the report
    document.body.classList.add('modal-open-message_lightbox_gallery');
    setShowModal(true);

  };

const handleShowSaveToListModal = (userId) => {
  console.log('Show Save to List Modal for userId:', userId);
  document.body.classList.add('modal-open-message_lightbox_gallery');
  setShowSaveToListModal({ visible: true, userId });
};

const handleModalSubmit = async (e) => {
  e.preventDefault();
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${config.baseURL}/report-user`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        report_to: reportReason,
        reason: selectedReportReason,
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      // Show success toast and hide it after 2 seconds
      setToastMessage(data.message);
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setToastMessage(''); // Clear message
      }, 2000); // 2 seconds delay
      setErrorMessage(''); // Clear any previous error message
    } else {
      // Show error message and hide it after 2 seconds
      setErrorMessage(data.message);
      setTimeout(() => {
        setErrorMessage(''); // Clear error message
      }, 2000); // 2 seconds delay
    }
  } catch (error) {
    // Show error message for fetch failure and hide it after 2 seconds
    setErrorMessage('Error reporting user');
    setTimeout(() => {
      setErrorMessage(''); // Clear error message
    }, 2000); // 2 seconds delay
  } finally {
    // Hide the modal in all cases
    setShowModal(false);
  }
};


  const handleSaveToListClick = () => {
    setShowSaveToListModal(true);
  };

  const handleCreateListClick = () => {
    // setShowSaveToListModal(false);
    setShowCreateListModal(true);
  };

  const handleCloseModal = () => {
    document.body.classList.remove('modal-open-message_lightbox_gallery');
    setShowModal(false);
  };

  const reportReasons = [
    'Violates OnlyFans Terms of Service',
    'Contains copyrighted material (DMCA)',
    'Child Sexual Abuse Material (CSAM)',
    'Report Spam',
    'Report Abuse'
  ];

  useEffect(() => {
    // Initialize the checked state based on userLists if needed
  }, [userLists]);


const handleCreateListSubmit = async (e) => {
  e.preventDefault();
  try {
    // Validate list name
    if (listName.trim() === '') {
      setErrorMessage('List name cannot be empty');
      setTimeout(() => setErrorMessage(''), 2000); // Clear error message after 2 seconds
      return;
    }
    if (listName.length > 64) {
      setErrorMessage('List name cannot exceed 64 characters');
      setTimeout(() => setErrorMessage(''), 2000); // Clear error message after 2 seconds
      return;
    }

    const token = localStorage.getItem('token');
    const response = await fetch(`${config.baseURL}/create-user-list`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({ title: listName }) // Use 'title' instead of 'list_name'
    });

    const data = await response.json();
    
    // If success
    if (data.success) {
      setToastMessage(data.message);
      setTimeout(() => setToastMessage(''), 2000); // Clear success message after 2 seconds
      setShowCreateListModal(false); // Close modal
      setListName(''); // Clear list name input
      setWordCount(0); // Reset word count
    } else {
      // If error from server response
      setErrorMessage(data.message);
      setTimeout(() => setErrorMessage(''), 2000); // Clear error message after 2 seconds
    }
  } catch (error) {
    setErrorMessage('Error creating list');
    setTimeout(() => setErrorMessage(''), 2000); // Clear error message after 2 seconds
  }
};

const hideUserPosts = async () => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.post(`${config.baseURL}/hide-users-post`, 
      { hidden_user_id: userId }, 
      {
        headers: { 
          Authorization: `Bearer ${token}` 
        }
      }
    );

    const data = response.data;

    // Check if the request was successful
    if (data.success) {
      setToastMessage(data.message || 'User\'s posts hidden successfully!');
      setTimeout(() => setToastMessage(''), 2000); // Clear success message after 2 seconds

      // Optional: Reload the page or trigger another state update after success
      window.location.reload(); // Uncomment if page reload is needed

    } else {
      // Handle error returned from the server
      setErrorMessage(data.message || 'Failed to hide user\'s posts. Please try again.');
      setTimeout(() => setErrorMessage(''), 2000); // Clear error message after 2 seconds
    }

  } catch (error) {
    // Handle network or other errors
    setErrorMessage('Failed to hide user\'s posts. Please try again.');
    setTimeout(() => setErrorMessage(''), 2000); // Clear error message after 2 seconds
  }
};

  return (
    <div>
      {toastMessage && (
        <div className={`toast-message ${toastType}`}>
          {toastMessage}
        </div>
      )}
      
        <div className="drop_cover_icon head_dropdown dropdown">
      <ul>
        <li className="nav-item dropdown">
          <Link
            className="nav-link dropdown-toggle chat_user_dropdown"
            id={`productsDropdown-${user.user_id}`}
            role="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <img
              className="icon_dropdown"
              src={white_drop_icon}
              alt="Dropdown"
            />
          </Link>
          <ul
            className="dropdown-menu"
            aria-labelledby={`productsDropdown-${user.user_id}`}
          >
            <li className="dropdown">
              <Link className="dropdown-item" onClick={() => handleCopyLink(user)}>
                Copy link to profile
              </Link>
            </li>
            <li className="dropdown">
              <Link className="dropdown-item" onClick={() => handleShowSaveToListModal(userId)}>
                Add to / remove from lists
              </Link>
            </li>

            <li className="dropdown">
              <Link className="dropdown-item danger_link" onClick={hideUserPosts}>
                 {isUserPostHidden ? "Show user's posts in feed" : "Hide user's posts from feed"}
              </Link>
            </li>


            <hr className="dropdown-divider" /> 

            <li className="dropdown">
              <Link
                className="dropdown-item danger_link"
                onClick={() => handleRestrictUser(userId)}
              >
                {restrictedUsers.has(userId) ? 'Unrestrict' : 'Restrict'}
              </Link>
            </li>
            <li className="dropdown">
              <Link
                className={`dropdown-item ${blockedUsers.has(userId) ? 'danger_link' : 'success_link'}`}
                onClick={() => blockedUsers.has(userId) ? handleUnblockUser(userId) : handleBlockUser(userId)}
              >
                {blockedUsers.has(userId) ? 'Unblock' : 'Block'}
              </Link>
            </li>
            <li className="dropdown">
              <Link
                className="dropdown-item danger_link"
                onClick={() => handleReportUser(userId)}
              >
                Report
              </Link>
            </li>
          </ul>
        </li>
      </ul>
    </div>


    <div className="share_text">
         {errorMessage && <div className="error_message">{errorMessage}</div>}
         {showToast && <div className="toast_message">{toastMessage}</div>}
    </div>

      {/* Save to List Modal */}
      {showSaveToListModal.visible && (
          <>
            <div className="modal-overlay-dark" onClick={() => setShowSaveToListModal({ visible: false, userId: null })}></div>
            <div className="modal restoreAccessModalAccess save_list_popup fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="saveToListModalLabel" aria-hidden="true">
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="saveToListModalLabel">SAVE TO LIST</h5>
                  </div>
                  <div className="modal-body">
              {isLoading ? (
  <div className="loading-container">
     <img src={msgload} alt="Loading..." />
  </div>
) : (
  <ul>
    {userLists.map(list => (
      <li key={list.id}>
        <div className="list_wrap_popup">
          <div className="m-checkbox-row checkbox_table">
            <input
              type="checkbox"
              checked={checkedLists.has(list.id)}
              onChange={(e) => handleCheckboxChange(list.id, userId, e.target.checked)}
              name=""
              id={`checkbox-${list.id}`}
            />
            <label htmlFor={`checkbox-${list.id}`} className="g-input__wrapper"></label>
          </div>
          <div className="list_popup">
            <div className="list_popup_left">
              <span>{list.title}</span>
              <small>{list.user_count} user{list.user_count > 1 ? 's' : ''}</small>
            </div>
            <div className="list_popup_right">
              {list.users && list.users.length > 0 ? (
                list.users.map(user => (
                  user.profile_image && (
                    <img
                      key={user.id}
                      src={user.profile_image}
                      alt={user.name}
                      style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                    />
                  )
                ))
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>
      </li>
    ))}
  </ul>
)}

          </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-primary cancel_btn" onClick={handleCreateListClick}>
                      <svg
                        enableBackground="new 0 0 50 50"
                        height="20px"
                        id="Layer_1"
                        version="1.1"
                        viewBox="0 0 50 50"
                        width="20px"
                        xmlSpace="preserve"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                      >
                        <rect fill="none" height={20} width={20} />
                        <line
                          fill="none"
                          stroke="#00aff0"
                          strokeMiterlimit={10}
                          strokeWidth={4}
                          x1={9}
                          x2={41}
                          y1={25}
                          y2={25}
                        />
                        <line
                          fill="none"
                          stroke="#00aff0"
                          strokeMiterlimit={10}
                          strokeWidth={4}
                          x1={25}
                          x2={25}
                          y1={9}
                          y2={41}
                        />
                      </svg>
                      New list
                    </button>
                    <button type="button" className="btn btn-secondary cancel_btn" onClick={() => setShowSaveToListModal({ visible: false, userId: null })}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      {/* Create New List Modal */}
      {showCreateListModal && (
        <>
          <div className="modal-overlay-dark" onClick={() => setShowCreateListModal(false)}></div>
          <div id="restoreAccessForm" className="modal restoreAccessModalAccess fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="createListModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="createListModalLabel">CREATE NEW LIST</h5>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleCreateListSubmit}>
                    <div className="position_relative">
                      <input
                        type="text"
                        className="form-control"
                        value={listName}
                        onChange={(e) => {
                          setListName(e.target.value);
                          setWordCount(e.target.value.length);
                        }}
                        maxLength="64"
                        placeholder=""
                      />
                      <label htmlFor="listname" className="placeholder-label">List Name</label>
                      <div className="word_count">{wordCount} / 64</div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-primary send_btn" onClick={() => setShowCreateListModal(false)}>Cancel</button>
                      <button type="submit" className="btn btn-primary send_btn">Save</button>
                    </div>
                  </form>
                  {apiResponseMessage && <p>{apiResponseMessage}</p>}
                </div>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Toast for user feedback */}
      {showToast && (
        <div className="toast">
          <p>{toastMessage}</p>
        </div>
      )}
      {errorMessage && (
        <div className="error">
          <p>{errorMessage}</p>
        </div>
      )}

       {showModal && (
          <>
            <div className="modal-overlay-dark" onClick={handleCloseModal}></div>
            <div className="modal restoreAccessModalAccess fade show" style={{ display: 'block' }} tabIndex="-1" aria-labelledby="restoreAccessModalLabel" aria-hidden="true">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="restoreAccessModalLabel">Report</h5>
                  </div>
                  <div className="modal-body">
                    <form id="reportUserForm" onSubmit={handleModalSubmit}>
                      <div className="form-group">
                        {reportReasons.map((reason, index) => (
                          <div className="form-check" key={index}>
                            <input 
                              className="form-check-input" 
                              type="radio" 
                              name="reportReason" 
                              id={`reportReason${index}`} 
                              value={reason} 
                              onChange={(e) => setSelectedReportReason(e.target.value)} 
                              required 
                            />
                            <label className="form-check-label" htmlFor={`reportReason${index}`}>
                              {reason}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary cancel_btn" onClick={handleCloseModal}>Cancel</button>
                        <button type="submit" className="btn btn-primary send_btn" disabled={!selectedReportReason}>Submit</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

    </div>
  );
};

export default UserHeaderDropdown;


